import { useCallback, useEffect, useState } from "react";
import { addSuccess } from "@42.nl/react-flash-messages/lib";
import { type Url, urlBuilder, useQueryParams } from "@42.nl/react-url/lib";
import { Button, SearchInput } from "@42.nl/ui";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { Card, Col, Input, Modal, ModalBody, Row } from "reactstrap";
import useLabelsApi from "@services/api/labels/labels.api";
import { LabelProps } from "@services/api/labels/labels.types";
import { AppFrame, DataTable, Loader } from "../components";
import { DeleteConfirmButton } from "../components/DeleteButton/DeleteConfirmButton";
import ModalHeaderCustom from "../components/Modal/ModalHeaderCustom";
import { type Paged } from "../components/PaginationBar/Paged";
import LabelForm, { type LabelFormData } from "./LabelForm";
import { LABEL_APP_URL } from "./links";

type LabelListQueryParams = {
  text: string;
  language: string;
} & Paged;

export default function LabelList() {
  const { t } = useTranslation(["label", "translation"]);
  const navigate = useNavigate();
  const location = useLocation();
  const [languages, setLanguages] = useState<string[]>();
  const [labelForm, setLabelForm] = useState<LabelFormData>();

  const {
    usePageQuery,
    useRemoveMutation,
    useLanguagesApi,
    useSaveMutation,
    invalidate
  } = useLabelsApi();

  const defaultLabelListQueryParams = useCallback(
    (): LabelListQueryParams => ({
      text: "",
      language: languages?.length === 1 ? languages[0].toUpperCase() : "",
      page: 1,
      size: 10,
      sort: "key,ASC"
    }),
    [languages]
  );

  const queryParams = useQueryParams<LabelListQueryParams>({
    location,
    defaultQueryParams: defaultLabelListQueryParams(),
    debugName: "LabelList"
  });

  const state = usePageQuery({
    variables: queryParams
  });
  const { data: fetchedLanguages } = useLanguagesApi();

  useEffect(() => {
    if (fetchedLanguages) {
      setLanguages(fetchedLanguages.map((language) => language.toUpperCase()));
    }
  }, [fetchedLanguages]);

  const toLabelList = useCallback(
    (queryParams?: Partial<LabelListQueryParams>): Url =>
      urlBuilder({
        url: LABEL_APP_URL,
        queryParams,
        defaultQueryParams: defaultLabelListQueryParams()
      }),
    [defaultLabelListQueryParams]
  );

  function changeSort(sort: string) {
    navigate(toLabelList({ ...queryParams, sort }), { replace: true });
  }

  function pageChanged(page: number, size: number) {
    navigate(toLabelList({ ...queryParams, page, size }), { replace: true });
  }

  function onSearch(text: string) {
    navigate(toLabelList({ ...queryParams, text, page: 1 }), { replace: true });
  }

  function filterChanged(name: string, value: string) {
    navigate(toLabelList({ ...queryParams, [name]: value, page: 1 }), {
      replace: true
    });
  }

  const columns = [
    {
      label: t("label:COLUMNS.KEY"),
      sort: "key",
      width: 100,
      cell: (row: LabelProps) => row.key
    },
    {
      label: t("label:COLUMNS.LANGUAGE"),
      sort: "language",
      width: 30,
      filter: () => (
        <Input
          type="select"
          value={queryParams.language}
          onChange={(event) => filterChanged("language", event.target.value)}
        >
          <option value={""}>{t("label:COLUMNS.ALL_LANGUAGES")}</option>
          {languages?.map((language) => (
            <option key={language} value={language}>
              {language}
            </option>
          ))}
        </Input>
      ),
      cell: (row: LabelProps) => row.language
    },
    {
      label: t("label:COLUMNS.VALUE"),
      sort: "value",
      width: 100,
      cell: (row: LabelProps) => row.value
    },
    {
      label: "",
      width: 50,
      cell: (row: LabelProps) => (
        <div className="actions">
          <Button
            className="me-1"
            color="primary"
            onClick={() => setLabelForm(row)}
            icon="edit"
          />
          <DeleteConfirmButton
            name={row.key}
            onDelete={() => deleteLabel(row)}
          />
        </div>
      )
    }
  ];

  async function onSubmit(data: LabelFormData) {
    await useSaveMutation.mutationFn(data);
    await invalidate();
    setLabelForm(undefined);
    addSuccess({
      text: t("translation:SAVE_SUCCESS", { key: data.key })
    });
  }

  async function deleteLabel(labelToDelete: LabelProps) {
    await useRemoveMutation.mutationFn(labelToDelete.id);
    await invalidate();
    addSuccess({
      text: t("translation:DELETE_SUCCESS", { name: labelToDelete.key })
    });
  }

  function newLabel() {
    let language;
    if (languages?.length === 1) {
      language = languages[0].toLocaleUpperCase();
    } else {
      language = queryParams.language;
    }

    return { language };
  }

  return (
    <AppFrame title={t("label:LABELS")}>
      <Row className="justify-content-center">
        <Col xs={12} sm={12} md={12} lg={12} xl={12}>
          <Card body>
            <div className="d-flex justify-content-end mb-2">
              <Button
                color="primary"
                icon="add"
                onClick={() => setLabelForm(newLabel())}
              >
                {t("label:ADD_LABEL")}
              </Button>
            </div>

            <SearchInput
              id="search"
              defaultValue={queryParams.text}
              onChange={(value) => onSearch(value)}
              debounce={500}
              label={t("label:ACTIONS.SEARCH")}
              hiddenLabel={true}
            />

            {labelForm ? (
              <Modal isOpen={!!labelForm}>
                <ModalHeaderCustom onClose={() => setLabelForm(undefined)}>
                  {labelForm.id
                    ? t("label:CHANGE_LABEL")
                    : t("label:ADD_LABEL")}
                </ModalHeaderCustom>
                <ModalBody>
                  <LabelForm
                    initialValues={labelForm}
                    onCancel={() => setLabelForm(undefined)}
                    onSubmit={onSubmit}
                  />
                </ModalBody>
              </Modal>
            ) : undefined}

            <Loader state={state}>
              {(page) => (
                <DataTable<LabelProps>
                  width={200}
                  columns={columns}
                  page={page}
                  sort={queryParams.sort}
                  onSort={changeSort}
                  onPage={pageChanged}
                />
              )}
            </Loader>
          </Card>
        </Col>
      </Row>
    </AppFrame>
  );
}
